import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  Building2,
  Plus,
  Search,
  Filter,
  MoreVertical,
  MapPin,
  Home,
  Users,
  DollarSign,
  ChevronRight,
  Grid3X3,
  List,
  Loader2,
  AlertCircle,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { propertyApi, Property } from '@/lib/api';
import AddPropertyDialog from '@/components/properties/AddPropertyDialog';

type ViewMode = 'grid' | 'list';

interface PropertyWithStats extends Property {
  occupiedUnits: number;
  monthlyRent: number;
}

export default function PropertiesPage() {
  const [viewMode, setViewMode] = useState<ViewMode>('grid');
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);

  const { data: properties = [], isLoading, error } = useQuery({
    queryKey: ['properties'],
    queryFn: async () => {
      const response = await propertyApi.getAll();
      return response.data;
    },
  });

  // Transform properties to include computed stats
  const propertiesWithStats: PropertyWithStats[] = properties.map((p) => ({
    ...p,
    occupiedUnits: 0, // TODO: Get from units API
    monthlyRent: 0, // TODO: Get from units API
  }));

  const filteredProperties = propertiesWithStats.filter((property) => {
    const fullAddress = `${property.address.line1}, ${property.address.city}, ${property.address.province}`;
    const matchesSearch =
      property.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      fullAddress.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesStatus =
      statusFilter === 'all' ||
      property.status.toLowerCase() === statusFilter.toLowerCase();
    return matchesSearch && matchesStatus;
  });

  const totalUnits = propertiesWithStats.reduce((sum, p) => sum + p.unitCount, 0);
  const occupiedUnits = propertiesWithStats.reduce((sum, p) => sum + p.occupiedUnits, 0);
  const totalMonthlyRent = propertiesWithStats.reduce((sum, p) => sum + p.monthlyRent, 0);
  const occupancyRate = totalUnits > 0 ? Math.round((occupiedUnits / totalUnits) * 100) : 0;

  if (isLoading) {
    return (
      <div className="flex h-96 items-center justify-center">
        <div className="flex flex-col items-center gap-4">
          <Loader2 className="h-8 w-8 animate-spin text-primary-600" />
          <p className="text-gray-500">Loading properties...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex h-96 items-center justify-center">
        <div className="flex flex-col items-center gap-4 text-center">
          <AlertCircle className="h-12 w-12 text-red-500" />
          <h3 className="text-lg font-medium text-gray-900">Failed to load properties</h3>
          <p className="text-sm text-gray-500">Please try again later</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Properties</h1>
          <p className="mt-1 text-sm text-gray-500">
            Manage your rental properties and units
          </p>
        </div>
        <Button className="gap-2" onClick={() => setIsAddDialogOpen(true)}>
          <Plus className="h-4 w-4" />
          Add Property
        </Button>
      </div>

      {/* Add Property Dialog */}
      <AddPropertyDialog
        isOpen={isAddDialogOpen}
        onClose={() => setIsAddDialogOpen(false)}
      />

      {/* Stats Cards */}
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <StatCard
          title="Total Properties"
          value={properties.length.toString()}
          icon={Building2}
          trend="+2 this month"
          trendUp={true}
        />
        <StatCard
          title="Total Units"
          value={totalUnits.toString()}
          icon={Home}
          subtitle={`${occupiedUnits} occupied`}
        />
        <StatCard
          title="Occupancy Rate"
          value={`${occupancyRate}%`}
          icon={Users}
          trend={occupancyRate >= 90 ? 'Excellent' : occupancyRate >= 75 ? 'Good' : 'Needs attention'}
          trendUp={occupancyRate >= 75}
        />
        <StatCard
          title="Monthly Revenue"
          value={`$${totalMonthlyRent.toLocaleString()}`}
          icon={DollarSign}
          trend="+$400 vs last month"
          trendUp={true}
        />
      </div>

      {/* Filters and Search */}
      <div className="flex flex-col gap-4 rounded-xl bg-white p-4 shadow-sm sm:flex-row sm:items-center sm:justify-between">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
          <input
            type="text"
            placeholder="Search properties..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="h-10 w-full rounded-lg border border-gray-200 bg-gray-50 pl-10 pr-4 text-sm transition-all focus:border-primary-500 focus:bg-white focus:outline-none focus:ring-2 focus:ring-primary-500/20"
          />
        </div>

        <div className="flex items-center gap-3">
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="h-10 rounded-lg border border-gray-200 bg-white px-3 text-sm focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-500/20"
          >
            <option value="all">All Status</option>
            <option value="active">Active</option>
            <option value="vacant">Vacant</option>
            <option value="maintenance">Maintenance</option>
          </select>

          <Button variant="outline" size="icon" className="hidden sm:flex">
            <Filter className="h-4 w-4" />
          </Button>

          <div className="flex rounded-lg border border-gray-200 p-1">
            <button
              onClick={() => setViewMode('grid')}
              className={cn(
                'rounded-md p-1.5 transition-colors',
                viewMode === 'grid'
                  ? 'bg-primary-100 text-primary-700'
                  : 'text-gray-500 hover:text-gray-700'
              )}
            >
              <Grid3X3 className="h-4 w-4" />
            </button>
            <button
              onClick={() => setViewMode('list')}
              className={cn(
                'rounded-md p-1.5 transition-colors',
                viewMode === 'list'
                  ? 'bg-primary-100 text-primary-700'
                  : 'text-gray-500 hover:text-gray-700'
              )}
            >
              <List className="h-4 w-4" />
            </button>
          </div>
        </div>
      </div>

      {/* Properties Grid/List */}
      {viewMode === 'grid' ? (
        <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
          {filteredProperties.map((property) => (
            <PropertyCard key={property.id} property={property} />
          ))}
        </div>
      ) : (
        <div className="space-y-3">
          {filteredProperties.map((property) => (
            <PropertyListItem key={property.id} property={property} />
          ))}
        </div>
      )}

      {filteredProperties.length === 0 && (
        <div className="flex flex-col items-center justify-center rounded-xl bg-white py-16 text-center shadow-sm">
          <Building2 className="h-12 w-12 text-gray-300" />
          <h3 className="mt-4 text-lg font-medium text-gray-900">No properties found</h3>
          <p className="mt-1 text-sm text-gray-500">
            {searchQuery
              ? 'Try adjusting your search or filters'
              : 'Get started by adding your first property'}
          </p>
          {!searchQuery && (
            <Button className="mt-4 gap-2">
              <Plus className="h-4 w-4" />
              Add Property
            </Button>
          )}
        </div>
      )}
    </div>
  );
}

// Stat Card Component
interface StatCardProps {
  title: string;
  value: string;
  icon: React.ElementType;
  trend?: string;
  trendUp?: boolean;
  subtitle?: string;
}

function StatCard({ title, value, icon: Icon, trend, trendUp, subtitle }: StatCardProps) {
  return (
    <div className="rounded-xl bg-white p-5 shadow-sm transition-shadow hover:shadow-md">
      <div className="flex items-start justify-between">
        <div>
          <p className="text-sm font-medium text-gray-500">{title}</p>
          <p className="mt-1 text-2xl font-bold text-gray-900">{value}</p>
          {subtitle && <p className="mt-0.5 text-xs text-gray-500">{subtitle}</p>}
          {trend && (
            <p
              className={cn(
                'mt-1 text-xs font-medium',
                trendUp ? 'text-green-600' : 'text-amber-600'
              )}
            >
              {trend}
            </p>
          )}
        </div>
        <div className="rounded-lg bg-primary-50 p-2.5">
          <Icon className="h-5 w-5 text-primary-600" />
        </div>
      </div>
    </div>
  );
}

// Property Card Component
interface PropertyCardProps {
  property: PropertyWithStats;
}

function PropertyCard({ property }: PropertyCardProps) {
  const occupancyPercent =
    property.unitCount > 0
      ? Math.round((property.occupiedUnits / property.unitCount) * 100)
      : 0;

  const fullAddress = `${property.address.line1}, ${property.address.city}, ${property.address.province}`;

  return (
    <Link
      to={`/dashboard/properties/${property.id}`}
      className="group block overflow-hidden rounded-xl bg-white shadow-sm transition-all hover:shadow-lg"
    >
      {/* Property Image */}
      <div className="relative h-40 bg-gradient-to-br from-primary-100 to-primary-200">
        <div className="absolute inset-0 flex items-center justify-center">
          <Building2 className="h-16 w-16 text-primary-300" />
        </div>
        <div className="absolute right-3 top-3">
          <span
            className={cn(
              'rounded-full px-2.5 py-1 text-xs font-medium',
              property.status === 'Active'
                ? 'bg-green-100 text-green-700'
                : property.status === 'Vacant'
                  ? 'bg-amber-100 text-amber-700'
                  : 'bg-gray-100 text-gray-700'
            )}
          >
            {property.status}
          </span>
        </div>
      </div>

      {/* Property Info */}
      <div className="p-4">
        <div className="flex items-start justify-between">
          <div className="flex-1">
            <h3 className="font-semibold text-gray-900 group-hover:text-primary-600">
              {property.name}
            </h3>
            <div className="mt-1 flex items-center gap-1 text-sm text-gray-500">
              <MapPin className="h-3.5 w-3.5" />
              <span className="truncate">{fullAddress}</span>
            </div>
          </div>
          <button
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
            }}
            className="rounded-lg p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-600"
          >
            <MoreVertical className="h-4 w-4" />
          </button>
        </div>

        <div className="mt-4 flex items-center justify-between border-t pt-4">
          <div className="flex items-center gap-4">
            <div className="text-center">
              <p className="text-lg font-semibold text-gray-900">{property.unitCount}</p>
              <p className="text-xs text-gray-500">Units</p>
            </div>
            <div className="h-8 w-px bg-gray-200" />
            <div className="text-center">
              <p className="text-lg font-semibold text-gray-900">{occupancyPercent}%</p>
              <p className="text-xs text-gray-500">Occupied</p>
            </div>
          </div>
          <div className="text-right">
            <p className="text-lg font-semibold text-gray-900">
              ${property.monthlyRent.toLocaleString()}
            </p>
            <p className="text-xs text-gray-500">/month</p>
          </div>
        </div>
      </div>
    </Link>
  );
}

// Property List Item Component
function PropertyListItem({ property }: PropertyCardProps) {
  const occupancyPercent =
    property.unitCount > 0
      ? Math.round((property.occupiedUnits / property.unitCount) * 100)
      : 0;

  const fullAddress = `${property.address.line1}, ${property.address.city}, ${property.address.province}`;

  return (
    <Link
      to={`/dashboard/properties/${property.id}`}
      className="group flex items-center gap-4 rounded-xl bg-white p-4 shadow-sm transition-all hover:shadow-md"
    >
      {/* Property Image */}
      <div className="flex h-16 w-16 flex-shrink-0 items-center justify-center rounded-lg bg-gradient-to-br from-primary-100 to-primary-200">
        <Building2 className="h-8 w-8 text-primary-400" />
      </div>

      {/* Property Info */}
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <h3 className="font-semibold text-gray-900 group-hover:text-primary-600">
            {property.name}
          </h3>
          <span
            className={cn(
              'rounded-full px-2 py-0.5 text-xs font-medium',
              property.status === 'Active'
                ? 'bg-green-100 text-green-700'
                : property.status === 'Vacant'
                  ? 'bg-amber-100 text-amber-700'
                  : 'bg-gray-100 text-gray-700'
            )}
          >
            {property.status}
          </span>
        </div>
        <div className="mt-0.5 flex items-center gap-1 text-sm text-gray-500">
          <MapPin className="h-3.5 w-3.5 flex-shrink-0" />
          <span className="truncate">{fullAddress}</span>
        </div>
      </div>

      {/* Stats */}
      <div className="hidden items-center gap-6 sm:flex">
        <div className="text-center">
          <p className="font-semibold text-gray-900">{property.unitCount}</p>
          <p className="text-xs text-gray-500">Units</p>
        </div>
        <div className="text-center">
          <p className="font-semibold text-gray-900">{occupancyPercent}%</p>
          <p className="text-xs text-gray-500">Occupied</p>
        </div>
        <div className="text-right">
          <p className="font-semibold text-gray-900">
            ${property.monthlyRent.toLocaleString()}
          </p>
          <p className="text-xs text-gray-500">/month</p>
        </div>
      </div>

      <ChevronRight className="h-5 w-5 flex-shrink-0 text-gray-400 group-hover:text-primary-600" />
    </Link>
  );
}
